import { NotFound } from '@components/404'
import { Layout } from '@components/common'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import { ProductInfo } from '@components/corra/ProductInfo'
import { Breadcrumbs } from '@corratech/pylot-breadcrumbs'
import { useAddToCart } from '@pylot-data/hooks/cartManager/use-add-to-cart'
import {
    GalleryTypeEnum,
    ProductGallery
} from '@corratech/pylot-product-gallery'
import { SEO, PAGE_TYPE } from '@corratech/pylot-seo'
import { useDataLayerAction } from '@corratech/pylot-tag-manager'
import { Container } from '@corratech/pylot-ui'
import { useUI } from '@corratech/pylot-ui/context'
import { DesignProductAttributes } from '@components/DesignProductAttributes/DesignProductAttributes'
import s from '@pagestyles/PDP.module.scss'
import getProduct from '@pylot-data/api/operations/get-product'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import {
    productSearchQuery,
    PdpStaticPathsQuery
} from '@pylot-data/hooks/product/graphql/productSearchQuery'
import { useCmsBlocks } from '@pylot-data/hooks/cms/use-cms-blocks'
import { useProductFetch } from '@pylot-data/hooks/product/use-product-fetch'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'
import { ConfigurableProduct } from '@pylot-data/pylotschema'
import cn from 'classnames'
import { getStoreConfig } from 'config'
import type {
    GetStaticPaths,
    GetStaticProps,
    GetStaticPropsContext,
    InferGetStaticPropsType
} from 'next'
import { useTranslation } from 'next-i18next/pages'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import { useRouter } from 'next/router'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { FrequentlyBoughtTogetherProducts } from '@components/corra/FrequentlyBoughtTogetherProducts'
import { ReviewsMobile, ReviewsDesktop } from '@components/Reviews'
import dynamic from 'next/dynamic'
import { useIsMobileScreen } from '@lib/utils/mediaQuery'
import { shouldPrerenderPaths } from '@lib/utils/staticPaths'
import { ReviewStars } from '@components/Reviews/ReviewStars'
import { useLowStockBadge } from '@lib/hooks/product/low-stock/useLowStockBadge'

// Custom hooks
import { usePDPData } from '@lib/hooks/product/pdp/usePDPData'
import { useStoreStock } from '@lib/hooks/product/pdp/useStoreStock'
import { usePDPAddToCart } from '@lib/hooks/product/pdp/usePDPAddToCart'
import { usePdpProductUrl } from '@lib/hooks/product/pdp/usePdpProductUrl'
import { usePdpLifecycleEffects } from '@lib/hooks/product/pdp/usePdpLifecycleEffects'
import { usePdpAccordionContents } from '@lib/hooks/product/pdp/usePdpAccordionContents'
import { useSuggestedSetMainProduct } from '@lib/hooks/product/pdp/useSuggestedSetMainProduct'
import { createWishlistButtonContent } from '@components/Product/pdp/blocks/PDPWishlistButtonContent'
import { PDPMainColumn } from '@components/Product/pdp/blocks/PDPMainColumn'
import WrappedFBTProductItem from '@components/Product/pdp/blocks/WrappedFBTProductItem'

const QRCodeModal = dynamic(() => import('@components/modals/QRCodeModal'), {
    ssr: false
})
const StoreStockModal = dynamic(
    () => import('@components/modals/StoreStockModal'),
    { ssr: false }
)

const FIRST_ACCORDION_PREFIX = 'accordion-metodos-de-pago'
const SECOND_ACCORDION_PREFIX = 'accordion-cambios-devoluciones'
const TIMEOUT_AFTER_OPENING_SIDEBAR = 50
const CATEGORY_URL_PREFIX = '/categoria'

export const getStaticPaths: GetStaticPaths = async () => {
    if (!shouldPrerenderPaths()) {
        return { paths: [], fallback: 'blocking' }
    }

    try {
        const config = getStoreConfig()
        const productSearch = await graphqlFetch<
            { pageSize: number; currentPage: number; search: string },
            PdpStaticPathsQuery
        >({
            query: productSearchQuery,
            variables: {
                pageSize: config.static.products.count,
                currentPage: 1,
                search: ''
            }
        })

        const paths = (productSearch.data.products.items ?? [])
            .map((item) => (item.url_key ?? '').trim())
            .filter(Boolean)
            .map((urlKey) => urlKey.replace(/\.html$/, ''))
            .map((urlKey) => ({
                params: { product_url: urlKey },
                locale: 'es'
            }))

        return { paths, fallback: 'blocking' }
    } catch (e) {
        console.log('PDP GETSTATICPATHS ERROR', e)
        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps = async ({
    locale,
    params
}: GetStaticPropsContext) => {
    const productUrlWithPostfix = params!.product_url as string
    try {
        const productUrl = productUrlWithPostfix.replace('.html', '')
        const product = await getProduct(productUrl, locale || '', {
            method: 'POST'
        })

        if (!product || !product?.productDetail?.items.length) {
            // revalidate corto: un error transitorio del backend llega aquí como
            // "sin items" y sin revalidate el 404 quedaría cacheado para siempre.
            return { notFound: true, revalidate: 60 }
        }

        return {
            props: {
                url_key: productUrl,
                productData: product,
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'pdp',
                    'wishlist',
                    'account'
                ]))
            },
            revalidate: 14400
        }
    } catch (e) {
        console.log('PDP GETSTATICPROPS ERROR', e)
        return {
            props: {
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'wishlist',
                    'account'
                ]))
            },
            // shell por error de backend: reintentar el SSR pronto, no en 4h.
            revalidate: 60
        }
    }
}

export default function PDP({
    url_key,
    productData
}: InferGetStaticPropsType<typeof getStaticProps>): ReactElement {
    // UI-only state stays here
    const [uiState, setUIState] = useState({
        displayQRCode: false,
        displayStoreStock: false
    })

    const router = useRouter()
    const { t } = useTranslation(['common', 'pdp', 'wishlist'])
    // SSR-safe: false en server y primer paint; el viewport real llega post-mount.
    // isMobileScreen() rompía hydration (ReviewsMobile vs ReviewsDesktop).
    const isMobile = useIsMobileScreen()

    const {
        openSidebar,
        openCartToast,
        openToast,
        closeSidebar,
        LoadingIndicator
    } = useUI()

    const { addToCart, isAdding } = useAddToCart()
    const dataLayerAction = useDataLayerAction()
    const config = getStoreConfig()

    const { isStackedGallery } = router.query
    const isStacked = isStackedGallery !== 'false'

    const productUrlFromRoute = usePdpProductUrl({
        urlKey: url_key,
        asPath: router.asPath
    })

    const {
        product,
        isSupportedProductType,
        isConfig,
        isBundle,
        isGiftCard,
        isGrouped,
        error,
        isValidating
    } = useProductFetch(
        { productUrl: productUrlFromRoute },
        {
            revalidateOnFocus: false,
            revalidateOnMount: true,
            initialData: { data: productData }
        }
    )

    const productFromProps = productData?.productDetail?.items?.[0]
    const resolvedProduct = product ?? productFromProps

    const { items: firstAccordionBlockContent } = useCmsBlocks(
        [FIRST_ACCORDION_PREFIX],
        { revalidateOnMount: true, revalidateOnFocus: false }
    )

    const { items: secondAccordionBlockContent } = useCmsBlocks(
        [SECOND_ACCORDION_PREFIX],
        { revalidateOnMount: true, revalidateOnFocus: false }
    )

    const {
        setSelectedOptions,
        selectedOptions,
        setQuantity,
        quantity,
        options,
        variant,
        mediaGalleryEntries,
        buttonLabel: buttonLabelWithoutAdding,
        isButtonDisabled,
        isConfigOptionSelected,
        selectedOptionUIDs
    } = useProductUI(resolvedProduct)

    // Derived PDP display data (badge text, effective ppum/is_set, rating summary, etc.)
    const pdpData = usePDPData(resolvedProduct, variant)

    // Store stock modal logic
    const {
        storeStockItems,
        emptyStoreStock,
        defaultStoreRegion,
        handleStock
    } = useStoreStock()

    // Memoized UI state updater
    const handleUIStateChange = useCallback(
        (updates: Partial<typeof uiState>) => {
            setUIState((prev) => ({ ...prev, ...updates }))
        },
        []
    )

    // Reviews scroll
    const scrollToReviews = useCallback(() => {
        const offsetValue = isMobile ? 200 : 250
        const elementId = isMobile ? 'review-section-mobile' : 'review-section'
        const element = document.getElementById(elementId)
        if (element) {
            window.scrollTo({
                top: element.offsetTop - offsetValue,
                behavior: 'smooth'
            })
        }
    }, [isMobile])

    useEffect(() => {
        if (typeof window === 'undefined' || !pdpData?.reviews) {
            return
        }

        if (window.location.hash !== '#reviews') {
            return
        }

        const timer = window.setTimeout(() => {
            scrollToReviews()
        }, 300)

        return () => window.clearTimeout(timer)
    }, [pdpData?.reviews, scrollToReviews])

    const reviewStars = useMemo(() => {
        if (!pdpData?.reviews) return null
        return (
            <button onClick={scrollToReviews} className={s['rating-container']}>
                <ReviewStars rating={pdpData.rating_summary} />
                {pdpData.ratingDisplay}
            </button>
        )
    }, [
        pdpData?.reviews,
        pdpData?.rating_summary,
        pdpData?.ratingDisplay,
        scrollToReviews
    ])

    const {
        addToCartHandler: addProductHandler,
        isAddingToCart,
        isOutOfStockByQty
    } = usePDPAddToCart({
        isBundle,
        product: resolvedProduct,
        variant,
        selectedOptionUIDs,
        quantity,
        setQuantity, // lets hook clamp if needed
        productUrl: productUrlFromRoute,
        addToCart,
        t,
        isMobile,
        openSidebar,
        closeSidebar,
        openToast,
        openCartToast,
        timeoutAfterOpeningSidebar: TIMEOUT_AFTER_OPENING_SIDEBAR
    })

    const replaceHtmlUrl = useCallback(
        (url: string) => {
            void router.replace(url, undefined, { scroll: false })
        },
        [router]
    )

    usePdpLifecycleEffects({
        asPath: router.asPath,
        productUrl: productUrlFromRoute,
        replace: replaceHtmlUrl,
        product: resolvedProduct,
        variant,
        dataLayerAction,
        setQuantity
    })

    const {
        name = pdpData?.name,
        short_description = pdpData?.short_description,
        description,
        price_range: productPriceRange = pdpData?.productPriceRange,
        badge_caption_icon: badgeCaptionIcon = pdpData?.badgeCaptionIcon
    } = (resolvedProduct || {}) as any

    const {
        sku = pdpData?.sku,
        price_range: variantPriceRange = pdpData?.variantPriceRange
    } = (variant || {}) as any

    const noVariantSelected = buttonLabelWithoutAdding === t('Select a variant')
    const hasQrCode = (resolvedProduct as any)?.qr_enabled === '1'

    const productForFBT = useMemo(() => {
        return {
            ...(resolvedProduct as any),
            customTextAfterProductImage: t('pdp||This product')
        }
    }, [resolvedProduct, t])

    const lowStockProduct = isConfig ? variant : resolvedProduct
    const hasGalleryBadge = Boolean(pdpData?.badgeText)
    const { show: showLowStockBadge, label: lowStockBadgeLabel } =
        useLowStockBadge(lowStockProduct)
    const galleryBadgeLabel =
        pdpData?.badgeText ||
        (showLowStockBadge ? lowStockBadgeLabel : undefined)
    const galleryBadgeVariant =
        !hasGalleryBadge && showLowStockBadge ? 'low-stock' : undefined

    const attrGroup = useMemo(() => {
        const disenoAttr = (resolvedProduct as any)?.diseno_para_ti
        return disenoAttr
            ? disenoAttr.split(',').map((item: string) => item.trim())
            : []
    }, [resolvedProduct])

    const wishlistButtonContent = useMemo(
        () => createWishlistButtonContent(t),
        [t]
    )
    const accordionContentsToShow = usePdpAccordionContents({
        t,
        product: resolvedProduct as ConfigurableProduct | undefined,
        shortDescription: short_description,
        firstAccordionBlockContent,
        secondAccordionBlockContent,
        isGrouped
    })

    const buttonLabel =
        isAdding || isAddingToCart
            ? t('Adding...')
            : buttonLabelWithoutAdding === 'Add to cart'
              ? t('pdp|Add to cart')
              : buttonLabelWithoutAdding

    const disabledAddToCart =
        isButtonDisabled ||
        isAddingToCart ||
        isAdding ||
        isOutOfStockByQty ||
        quantity === 0

    const { isSuggestedSet, mainProductForSet, mainProductCartForSet } =
        useSuggestedSetMainProduct({
            product: resolvedProduct,
            variant,
            description,
            shortDescription: short_description,
            variantPriceRange,
            productPriceRange,
            effectivePpum: pdpData?.effectivePpum,
            effectiveIsSet: pdpData?.effectiveIsSet
        })

    const ventiFinalPrice = useMemo(() => {
        const range = variantPriceRange ?? productPriceRange
        const fromSelectedRange =
            range?.minimum_price?.final_price?.value ??
            range?.maximum_price?.final_price?.value
        if (fromSelectedRange != null && fromSelectedRange > 0) {
            return fromSelectedRange
        }
        return (
            resolvedProduct?.price_range?.minimum_price?.final_price?.value ??
            resolvedProduct?.price_range?.maximum_price?.final_price?.value ??
            0
        )
    }, [variantPriceRange, productPriceRange, resolvedProduct?.price_range])

    const isPageLoading =
        !resolvedProduct && (isValidating || router.isFallback)

    if (isPageLoading) return <LoadingIndicator />
    if (!resolvedProduct && error) {
        return (
            <Container clean className={s['container-classes']}>
                <h1>{t('Error!!!')}</h1>
            </Container>
        )
    }
    if (!resolvedProduct) {
        return <NotFound identifier={config.base.route?.cms_no_route!} />
    }
    if (product && !isSupportedProductType) {
        return (
            <Container clean className={s['container-classes']}>
                <h1>{`${product?.__typename} ${t('is not supported')}`}</h1>
            </Container>
        )
    }

    const activeProduct = resolvedProduct

    return (
        <Container clean>
            <QRCodeModal
                isOpen={uiState.displayQRCode}
                onClose={() => handleUIStateChange({ displayQRCode: false })}
                sku={pdpData?.sku || ''}
                noVariantSelected={noVariantSelected}
                t={t}
            />

            <StoreStockModal
                isOpen={uiState.displayStoreStock}
                onClose={() =>
                    handleUIStateChange({ displayStoreStock: false })
                }
                storeStockItems={storeStockItems}
                emptyStoreStock={emptyStoreStock}
                defaultStoreRegion={defaultStoreRegion}
                onRegionChange={(value) =>
                    handleStock(value, pdpData?.sku || '')
                }
                t={t}
            />

            <div className={s['container-classes']}>
                <SEO product={activeProduct} page={PAGE_TYPE.PDP} />

                <div className={cn(s['pdp-breadcrumbs'], 'breadcrumbs')}>
                    {activeProduct.categories?.length ? (
                        <Breadcrumbs
                            product={activeProduct}
                            separator="|"
                            urlPrefix={CATEGORY_URL_PREFIX}
                            rootText={t('breadcrumbs|Home')}
                        />
                    ) : null}
                </div>

                <div className={s['pdp-container']}>
                    <div className={s['pdp-gallery-wrap']}>
                        <ProductGallery
                            mediaGalleryEntries={mediaGalleryEntries}
                            config={{
                                mobile: {
                                    gallerytype: GalleryTypeEnum.SCROLL,
                                    zoom: false,
                                    fullscreen: false,
                                    badgeLabel: galleryBadgeLabel,
                                    badgeVariant: galleryBadgeVariant
                                },
                                desktop: {
                                    gallerytype: isStacked
                                        ? GalleryTypeEnum.STACKED
                                        : GalleryTypeEnum.SCROLL,
                                    zoom: false,
                                    fullscreen: true,
                                    thumb: true,
                                    enableLastVideoAsFullWidth: true,
                                    imagesPerPage: 4,
                                    loadMoreLabel: t('pdp||Load More'),
                                    badgeLabel: galleryBadgeLabel,
                                    badgeVariant: galleryBadgeVariant
                                }
                            }}
                        />
                    </div>

                    <PDPMainColumn
                        t={t}
                        sku={sku}
                        hasQrCode={hasQrCode}
                        name={name}
                        isGiftCard={isGiftCard}
                        isGrouped={isGrouped}
                        isBundle={isBundle}
                        isConfig={isConfig}
                        product={activeProduct}
                        variantPriceRange={variantPriceRange}
                        productPriceRange={productPriceRange}
                        pdpData={{ ...pdpData, short_description }}
                        badgeCaptionIcon={badgeCaptionIcon}
                        ventiFinalPrice={ventiFinalPrice}
                        options={options}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        quantity={quantity}
                        setQuantity={setQuantity}
                        buttonLabelWithoutAdding={buttonLabelWithoutAdding}
                        addProductHandler={addProductHandler}
                        isAddingToCart={isAddingToCart}
                        isAdding={isAdding}
                        disabledAddToCart={disabledAddToCart}
                        isOutOfStockByQty={isOutOfStockByQty}
                        buttonLabel={buttonLabel}
                        selectedOptionUIDs={selectedOptionUIDs}
                        isConfigOptionSelected={isConfigOptionSelected}
                        wishlistButtonContent={wishlistButtonContent}
                        reviewStars={reviewStars}
                        onOpenQrCode={() =>
                            handleUIStateChange({ displayQRCode: true })
                        }
                        onOpenStoreStock={() =>
                            handleUIStateChange({ displayStoreStock: true })
                        }
                        isSuggestedSet={isSuggestedSet}
                        mainProductForSet={mainProductForSet}
                        mainProductCartForSet={mainProductCartForSet}
                        accordionContents={accordionContentsToShow}
                    />
                </div>
            </div>

            <div className={s['product-info']}>
                <ProductInfo description={description} />
            </div>

            {attrGroup.length > 0 && (
                <div className={s['container-classes']}>
                    <DesignProductAttributes disenado_para_ti={attrGroup} />
                </div>
            )}

            <div className={s['container-classes px-0']}>
                <FrequentlyBoughtTogetherProducts
                    currentProduct={{
                        ...(activeProduct as any),
                        customTextAfterProductImage: t('pdp||This product')
                    }}
                    currentProductPricing={
                        variantPriceRange || productPriceRange
                    }
                    currentProductSelectedOptionsUIDs={selectedOptionUIDs}
                    products={activeProduct?.fbt_products}
                    ProductTile={WrappedFBTProductItem}
                />
            </div>

            <div className={s['container-classes']}>
                <div id="exponea-carousel-nuestros-disenadores-te-sugieren-pdp" />
                <div id="exponea-carousel-recientemente-vistos-pdp" />
            </div>

            <div id="review-section-mobile" className={s['container-classes']}>
                {pdpData?.reviews && isMobile && (
                    <ReviewsMobile
                        reviews={pdpData.reviews}
                        ratingSummary={pdpData.rating_summary}
                        reviewCount={pdpData.review_count}
                        sku={activeProduct.sku}
                    />
                )}
            </div>

            <div id="review-section" className={s['container-classes']}>
                {pdpData?.reviews && !isMobile && (
                    <ReviewsDesktop
                        reviews={pdpData.reviews}
                        ratingSummary={pdpData.rating_summary}
                        reviewCount={pdpData.review_count}
                        sku={activeProduct.sku}
                    />
                )}
            </div>
        </Container>
    )
}

PDP.Layout = Layout
